import { FC, useRef, useState, MouseEvent, MutableRefObject } from 'react';

import { Box } from '@mui/material';

import { styled } from '@mui/material/styles';
import { AnalyticEventAction } from 'analytics';
import { trackActionButtonAnalyticsEvent } from 'analytics/events/action-button';

import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';

import { useStores } from 'mobx/hooks/useStores';

import { FEATURES } from 'constants/features';

import { PathwayBasicInfo } from 'models/PathwayTemplates';
import Ticket from 'models/Ticket';

import { useLogCallQuickActionVisibility } from 'hooks/useLogCallQuickActionVisibility';

import Icon from 'components/Icons/Icon';
import { SimpleTooltip } from 'components/Tooltip';

import { QuickActionContainer } from './QuickActionContainer';

import CallAndPathwaysDropDown from '../CallAndPathwaysDropDown';

type Props = {
  ticket: Ticket;
  ticketIndex: number;
  ticketSectionCurrentPage: number;
};

export const LogCallQuickAction: FC<Props> = observer(
  ({ ticket, ticketIndex, ticketSectionCurrentPage }) => {
    const history = useHistory();
    const { callLoggingStore, settingsStore } = useStores();

    const [isPathwaysDropdownOpen, setIsPathwaysDropdownOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const isVisible = useLogCallQuickActionVisibility(ticket);

    if (!isVisible) {
      return null;
    }

    const handleClick = (event: MouseEvent) => {
      event.stopPropagation();

      trackActionButtonAnalyticsEvent({
        action: AnalyticEventAction.LogCall,
        patient_id: ticket.patientId,
        ticket_id: ticket.id,
        item_index: ticketIndex + 1,
        page_number: ticketSectionCurrentPage + 1
      });

      if (settingsStore.hasFeature(FEATURES.ACTIONS_LOG_CALLS_PATHWAYS)) {
        setIsPathwaysDropdownOpen((isPathwaysDropdownOpen) => !isPathwaysDropdownOpen);
      } else {
        startCall();
      }
    };

    const startCall = (selectedPathway?: PathwayBasicInfo) => {
      setIsPathwaysDropdownOpen(false);

      const callOptions: {
        forTicketIds?: number[];
        selectedPathway?: PathwayBasicInfo;
        connectedTaskId?: number;
      } = {
        selectedPathway
      };

      if (ticket.isTask) callOptions.connectedTaskId = ticket.id;
      else callOptions.forTicketIds = [ticket.id];

      callLoggingStore.startCallSession(callOptions);

      if (!history.location.pathname.includes('patient')) {
        history.push(`/patient/${ticket.patientId}`);
      }
    };

    return (
      <div>
        <SimpleTooltip container={StyledTruncate} title={<Box p={8}>Log Call</Box>} placement="top">
          <QuickActionContainer
            ref={buttonRef}
            onClick={handleClick}
            data-test-hook={`log-call-quick-action-${ticket.id}`}
          >
            <Icon.PhoneGradient />
          </QuickActionContainer>
        </SimpleTooltip>
        <CallAndPathwaysDropDown
          ticket={ticket}
          ticketIds={[ticket.id]}
          tooltipReference={buttonRef as MutableRefObject<Element>}
          tooltipController={{
            visible: isPathwaysDropdownOpen,
            onClickOutside: () => setIsPathwaysDropdownOpen(false)
          }}
          onSelect={startCall}
          patientId={ticket.patientId}
          allowNoPathway
          ticketIndex={ticketIndex}
          ticketSectionCurrentPage={ticketSectionCurrentPage}
        />
      </div>
    );
  }
);

const StyledTruncate = styled('div')`
  overflow: unset;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
